import { ClickAwayListener, Grid, useMediaQuery } from "@mui/material";
import {
  useClickAway,
  useIntersectionObserver,
  useWindowScroll,
} from "@uidotdev/usehooks";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share";
import Color from "../../../assets/colors";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import useTriggerSnackbar from "../../../hooks/useTriggerSnackbar";
import {
  bookmarkCompany,
  getIndustries,
  unBookmarkCompany,
  updateSelectedCompany,
} from "../../../redux/actions/company_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../redux/actions/navbar_action";
import { checkAuthentication } from "../../../redux/actions/user_action";
import {
  CircularProgressStyled,
  CompanyLogo,
  FlexContainer,
  IndustryIconStyled,
  JobIconStyled,
  TextStyled,
} from "../styles";
import {
  ClipboardContainer,
  CompanyDetailWrapper,
  CompanyName,
  FilledButton,
  InfoCirleFilledStyled,
  LinkIconStyled,
  NavigationWrapper,
  NoBoldStyled,
  OutlineButton,
  ShareButton,
  ShareButtonsContainer,
  ShareIconStyled,
  StyledTooltip,
  StyledTooltipText,
  TabStyled,
  TabsContainer,
  TooltipInfoText,
} from "./styles";

function CompanyNavigation(props) {
  const { companyDetail, currentTab, setCurrentTab, companySocialExist } =
    props || {};

  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isTablet = useMediaQuery("(min-width: 640px)");
  const dispatch = useDispatch();

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const id = companyDetail?.id;

  const fetchingCompany = useSelector(
    (state) => state?.companies?.fetchingCompany
  );
  const bookmarking = useSelector(
    (state) => state.companies?.bookmarkingCompany
  );
  const unbookmarking = useSelector(
    (state) => state.companies?.unbookmarkCompany
  );
  const bookmark = useSelector(
    (state) => state.companies?.selectedCompany?.bookmark
  );
  const industriesList = useSelector((state) => state.companies.industries);

  const currentCompanyIndustry = industriesList?.filter(
    (industry) => industry.title == companyDetail?.industry
  );

  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    dispatch(getIndustries());
  }, [dispatch]);

  useEffect(() => {
    if (!fetchingCompany && bookmark !== null) {
      setShowButton(true);
    }
  }, [bookmark]);

  const [ref, entry] = useIntersectionObserver();
  const showDiv = entry?.isIntersecting;

  if (showDiv) {
    setCurrentTab(1);
  }

  const [{ y }, scrollTo] = useWindowScroll();

  const shareRef = useClickAway(() => {
    setShareIconsOpen(false);
  });

  const jobCount = companyDetail?.jobCount;
  const videoCount = companyDetail?.videoCount;
  const benefitsCount = companyDetail?.benefitsCount;

  const tabItems = companySocialExist
    ? [
        { name: "about", value: 2, id: "cdp-tab-about" },
        { name: "office", value: 1, id: "cdp-tab-office" },
        { name: "people", value: 3, id: "cdp-tab-people" },
        { name: "benefits", value: 4, id: "cdp-tab-benefits" },
        { name: "more", value: 5, id: "cdp-tab-more" },
      ]
    : [
        { name: "office", value: 1, id: "cdp-tab-office" },
        { name: "about", value: 2, id: "cdp-tab-about" },
        { name: "people", value: 3, id: "cdp-tab-people" },
        { name: "benefits", value: 4, id: "cdp-tab-benefits" },
        { name: "more", value: 5, id: "cdp-tab-more" },
      ];

  let filteredTab = [...tabItems]; // Create a copy of tabItems

  if (jobCount === 0) {
    // Remove the element with name "more"
    filteredTab = filteredTab.filter((item) => item.name !== "more");
  }

  if (videoCount === 0) {
    // Remove the element with name "benefits"
    filteredTab = filteredTab.filter((item) => item.name !== "people");
  }

  if (benefitsCount === 0) {
    // Remove the element with name "benefits"
    filteredTab = filteredTab.filter((item) => item.name !== "benefits");
  }

  function handleScrollTop() {
    scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleScrollToJob() {
    sendTrackingEvent({
      event: "CE_click-view-button-jobs-cdp",
      "company-id": id,
    });

    const headerOffset = 200;
    const elementPosition = document
      .getElementById("CompanyJobs")
      ?.getBoundingClientRect().top;
    const offsetPosition = elementPosition + y - headerOffset;
    scrollTo({ top: offsetPosition, behavior: "smooth" });
  }

  function handleScrollTo(newTab) {
    let section;

    switch (newTab) {
      case 1:
        section = document.getElementById("CompanyOffice");
        handleEventTab("office");
        break;
      case 2:
        section = document.getElementById("CompanyAbout");
        handleEventTab("about");
        break;
      case 3:
        section = document.getElementById("CompanyPeople");
        handleEventTab("people");
        break;
      case 4:
        section = document.getElementById("CompanyBenefits");
        handleEventTab("benefits");
        break;
      case 5:
        section = document.getElementById("CompanyJobs");
        handleEventTab("more");
        break;
      default:
        section = document.getElementById("CompanyOffice");
        handleEventTab("office");
    }

    const headerOffset = 200;
    const elementPosition = section?.getBoundingClientRect().top;
    const offsetPosition = elementPosition + y - headerOffset;
    scrollTo({ top: offsetPosition, behavior: "smooth" });
    setCurrentTab(newTab);
  }

  function handleEventTab(cTab) {
    sendTrackingEvent({
      event: "CE_click-company-view-tab",
      "company-id": companyDetail?.id,
      section: cTab,
    });
  }

  function handleFollow() {
    const loggedIn = checkAuthentication();

    if (!loggedIn) {
      return dispatch(updateSelectedCompany(companyDetail)).then(() => {
        dispatch(updateSignUpOrigin("save_company")).then(() => {
          dispatch(updateShowSignInModalStatus(true));
        });
      });
    }

    if (!bookmark) {
      sendTrackingEvent({
        event: "CE_follow-company-cdp",
        "company-id": id,
      });

      dispatch(bookmarkCompany({ companyId: id }));
    } else if (bookmark) {
      sendTrackingEvent({
        event: "CE_unfollow-company-cdp",
        "company-id": id,
      });

      dispatch(unBookmarkCompany({ companyId: id }));
    }
  }

  const [shareIconsOpen, setShareIconsOpen] = useState(false);

  function handleShare() {
    sendTrackingEvent({
      event: "CE_click-share-cdp",
      "company-id": id,
    });
    setShareIconsOpen(!shareIconsOpen);
  }

  function handleIndustryFilterHref() {
    let industrySlug = "";

    currentCompanyIndustry?.forEach((industry) => {
      industrySlug += "industry_ids=" + industry?.id + "&";
    });

    return (
      "/companies?" +
      industrySlug.slice(0, industrySlug.length - 1) +
      "&triggerOrigin=cdp-text"
    );
  }

  function TooltipChip() {
    return (
      <ClickAwayListener
        onClickAway={() => {
          setTooltipOpen(false);
        }}
      >
        <StyledTooltip
          open={tooltipOpen}
          onClick={() => {
            setTooltipOpen(true);
          }}
          onMouseEnter={() => {
            setTooltipOpen(true);
          }}
          onClose={() => {
            setTooltipOpen(false);
          }}
          componentsProps={{
            tooltip: {
              sx: {
                display: "flex",
                width: "234px",
                padding: "10px 14px",
                flexDirection: "column",
                alignItems: "center",
                borderRadius: "12px",
                border: "2px solid #222222",
                background: "#FFF",
              },
            },
          }}
          sx={{
            display: "flex !important",
            gap: "4px",
          }}
          title={
            <TooltipInfoText>
              {companyDetail?.name} is a{" "}
              <span style={{ fontWeight: 700 }}>headhunting firm</span>, the job
              openings listed here are posted on behalf of our clients
            </TooltipInfoText>
          }
          placement="bottom-start"
        >
          <StyledTooltipText>RECRUITMENT firm</StyledTooltipText>
          <InfoCirleFilledStyled />
        </StyledTooltip>
      </ClickAwayListener>
    );
  }

  return (
    <>
      <div ref={ref}></div>
      <NavigationWrapper smallView={!showDiv && isTablet}>
        <FlexContainer
          alignItems={"center"}
          gap={
            showDiv
              ? isTablet
                ? "1.5rem"
                : "1rem"
              : isDesktop
              ? "0.5rem"
              : "0"
          }
        >
          {companyDetail.logo ? (
            showDiv ? (
              <CompanyLogo
                src={companyDetail?.logo}
                alt={companyDetail?.name + " company logo"}
                aria-label="Company Logo"
                id="company-logo"
                height={"65px"}
                width={"65px"}
                style={{ cursor: "pointer" }}
                onClick={handleScrollTop}
              />
            ) : isDesktop ? (
              <CompanyLogo
                src={companyDetail?.logo}
                alt={companyDetail?.name + " company logo"}
                aria-label="Company Logo"
                id="company-logo"
                height={"48px"}
                width={"48px"}
                style={{ cursor: "pointer" }}
                onClick={handleScrollTop}
              />
            ) : (
              <CompanyLogo
                src={companyDetail?.logo}
                alt={companyDetail?.name + " company logo"}
                aria-label="Company Logo"
                id="company-logo"
                height={"30px"}
                width={"0px"}
                style={{ border: "none" }}
                onClick={handleScrollTop}
              />
            )
          ) : null}

          <CompanyDetailWrapper $showDiv={showDiv}>
            <Grid
              display={"flex"}
              alignItems={isDesktop ? "center" : "flex-start"}
              gap={"8px"}
            >
              <CompanyName id="company-name">{companyDetail?.name}</CompanyName>
              {companyDetail?.recruitmentFirm && <TooltipChip />}
            </Grid>
            <FlexContainer gap={"0.75rem"}>
              <Link
                href={handleIndustryFilterHref()}
                style={{ textDecoration: "none", color: "currentcolor" }}
              >
                <FlexContainer
                  alignItems={"center"}
                  gap={"0.5rem"}
                  sx={{ width: "auto", cursor: "pointer" }}
                  id="company-industry"
                >
                  <IndustryIconStyled />
                  <TextStyled
                    fontFamily={"Karla"}
                    fontSize={"0.875rem"}
                    lineHeight={"1.25rem"}
                    letterSpacing={"0.15px"}
                    whiteSpace={"nowrap"}
                  >
                    {companyDetail?.industry}
                  </TextStyled>
                </FlexContainer>
              </Link>
              {companyDetail?.jobCount > 0 ? (
                <FlexContainer
                  alignItems={"center"}
                  gap={"0.5rem"}
                  color={Color?.hiredlyPurple}
                  onClick={handleScrollToJob}
                  sx={{ width: "auto", cursor: "pointer" }}
                  id="company-job-count"
                >
                  <JobIconStyled />
                  <TextStyled
                    fontFamily={"Karla"}
                    fontSize={"0.875rem"}
                    lineHeight={"1.25rem"}
                    letterSpacing={"0.15px"}
                  >
                    {companyDetail?.jobCount + " jobs"}
                  </TextStyled>
                </FlexContainer>
              ) : null}
            </FlexContainer>
          </CompanyDetailWrapper>

          {showButton ? (
            <FlexContainer
              justifyContent={!isTablet && "space-between"}
              gap={!isTablet && "0.5rem"}
              sx={{ display: showDiv && "none" }}
              alignSelf={!isTablet && "stretch"}
              padding={!isTablet && "8px"}
            >
              <TabsContainer>
                {filteredTab?.map((item, index) => (
                  <TabStyled
                    key={index}
                    onClick={() => handleScrollTo(item?.value)}
                    selected={currentTab === item?.value}
                    id={item?.id}
                  >
                    {item?.name}
                  </TabStyled>
                ))}
              </TabsContainer>

              {!isTablet && companyDetail?.jobCount > 0 ? (
                <FilledButton
                  aria-label="view job button"
                  onClick={handleScrollToJob}
                  style={{
                    borderRadius: "0.375rem",
                    height: "2.5rem",
                    width: "fit-content",
                    padding: "0 0.625rem",
                  }}
                  id="company-view-jobs"
                >
                  <JobIconStyled />
                  <NoBoldStyled>
                    {"(" + companyDetail?.jobCount + ")"}
                  </NoBoldStyled>
                </FilledButton>
              ) : null}
            </FlexContainer>
          ) : null}
        </FlexContainer>

        {showButton ? (
          <FlexContainer
            gap={"0.5rem"}
            sx={{
              display: !isTablet && !showDiv && "none",
              width: isTablet ? "fit-content" : "100%",
              alignSelf: !isTablet && "stretch",
            }}
          >
            {companyDetail?.jobCount > 0 ? (
              <FilledButton
                aria-label="view job button"
                onClick={handleScrollToJob}
                id="company-view-jobs"
              >
                View Jobs
                <NoBoldStyled>
                  {"(" + companyDetail?.jobCount + ")"}
                </NoBoldStyled>
              </FilledButton>
            ) : null}
            <OutlineButton
              aria-label="follow company button"
              onClick={handleFollow}
              disabled={bookmarking}
              sx={
                bookmark && {
                  backgroundColor: Color?.black,
                  color: Color?.white,
                }
              }
              id="cdp-follow"
            >
              {bookmarking || unbookmarking ? (
                <CircularProgressStyled />
              ) : bookmark ? (
                "Following"
              ) : (
                "Follow"
              )}
            </OutlineButton>
            <div ref={shareRef}>
              <ShareButton
                aria-label="share company button"
                onClick={handleShare}
                id="cdp-share"
              >
                <ShareIconStyled />
              </ShareButton>

              {shareIconsOpen ? (
                <SharePopup companyDetail={companyDetail} showDiv={showDiv} />
              ) : null}
            </div>
          </FlexContainer>
        ) : null}
      </NavigationWrapper>
    </>
  );
}

function SharePopup(props) {
  const { companyDetail, showDiv } = props;

  const isDesktop = useMediaQuery("(min-width: 1280px)");

  const [triggerSnackbarFunc] = useTriggerSnackbar();
  const shareUrl = `${process.env.NEXT_PUBLIC_DOMAIN}/companies/${companyDetail?.slug}`;
  const title = `I found a company you might like!\n\n${companyDetail?.name}\n\nHave a look here`;

  async function handleCopyClipboard() {
    if (!navigator.clipboard) {
      // Clipboard API not available
      return;
    }
    const text = `${title}: ${shareUrl}`;
    try {
      await navigator.clipboard.writeText(text);
      handleEventTab("link");
      triggerSnackbarFunc({
        snackbarMessage: "Link copied successfully.",
        severity: "success",
      });
    } catch (error) {
      triggerSnackbarFunc({
        snackbarMessage: "Link failed to copy.",
        severity: "error",
      });
    }
  }

  function handleEventTab(shareMethod) {
    sendTrackingEvent({
      event: "CE_click-share-cdp",
      "company-id": companyDetail?.id,
      item: shareMethod,
    });
  }

  return (
    <ShareButtonsContainer
      sx={!showDiv && { top: isDesktop ? "30px" : "30px" }}
    >
      <FacebookShareButton
        url={shareUrl}
        quote={title}
        id={"facebook-job-link-sharing-button"}
        onClick={() => handleEventTab("facebook")}
      >
        <FacebookIcon size={32} round />
      </FacebookShareButton>
      <TwitterShareButton
        url={shareUrl}
        aria-label={title}
        id={"twitter-job-link-sharing-button"}
        onClick={() => handleEventTab("X")}
      >
        <TwitterIcon size={32} round />
      </TwitterShareButton>
      <LinkedinShareButton
        url={shareUrl}
        summary={title}
        id={"linkedin-job-link-sharing-button"}
        onClick={() => handleEventTab("linkedin")}
      >
        <LinkedinIcon size={32} round />
      </LinkedinShareButton>
      <WhatsappShareButton
        url={shareUrl}
        aria-label={title}
        separator=": "
        id={"whatsapp-job-link-sharing-button"}
        onClick={() => handleEventTab("whatsapp")}
      >
        <WhatsappIcon size={32} round />
      </WhatsappShareButton>
      <EmailShareButton
        url={shareUrl}
        subject={title}
        id={"email-job-link-sharing-button"}
        onClick={() => handleEventTab("email")}
      >
        <EmailIcon size={32} round />
      </EmailShareButton>
      <ClipboardContainer>
        <LinkIconStyled onClick={handleCopyClipboard} />
      </ClipboardContainer>
    </ShareButtonsContainer>
  );
}

export default CompanyNavigation;
